import ErrorStatus from '$shared/constants/ErrorStatus';
import ServiceError from '$shared/models/ServiceError';
import { sentry } from '../logger/sentry';
import UIErrorMessage from '$shared/constants/UIErrorMessage';
import { ServiceException } from '$shared/exceptions/ServiceException';
import { UIError } from '$shared/models/UIError';

const throwServiceError = (
  error: ServiceError | Error,
  methodName: string,
  args: any,
  finallyCondition?: (error: ServiceError) => void
): void => {
  // Skip the sentry log if the error is instance of UIError
  if (error instanceof UIError) throw error;

  if (error instanceof ServiceError) {
    finallyCondition?.(error);
    throw error;
  }

  const status = ErrorStatus.UnHandledException;
  const errorMessage = error?.message || UIErrorMessage.UnknowError;
  const serviceError = new ServiceError(status, errorMessage, errorMessage);
  error?.stack && (serviceError.stack = error.stack);

  const exception = ServiceException.from('ServiceErrorWrapper', methodName, args, error, {});
  sentry.log(exception);

  finallyCondition?.(serviceError);
  throw serviceError;
};

/**
 * Wrapper function to handle service errors (replaces decorator for SSR compatibility)
 * @param methodName - Name of the method being wrapped
 * @param fn - The async function to wrap
 * @param finallyCondition - Optional callback after error is thrown
 */
export function withServiceErrorHandling<T extends (...args: any[]) => Promise<any>>(
  methodName: string,
  fn: T,
  finallyCondition?: (error: ServiceError) => void
): T {
  return (async function (...args: any[]) {
    try {
      return await fn(...args);
    } catch (error: any) {
      throwServiceError(error, methodName, args, finallyCondition);
    }
  }) as T;
}

/**
 * Legacy decorator for backwards compatibility (may not work in SSR)
 * @deprecated Use withServiceErrorHandling function instead
 */
const ServiceErrorWrapper = (finallyCondition?: (error: ServiceError) => void) => {
  return (
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    _target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ): PropertyDescriptor => {
    const originalMethod = descriptor.value;
    descriptor.value = function (...args: any[]) {
      try {
        const result = originalMethod.apply(this, args);
        if (result && result instanceof Promise) {
          return result.catch((error: ServiceError | Error) => {
            throwServiceError(error, propertyKey, args, finallyCondition);
          });
        }
        return result;
      } catch (error: any) {
        throwServiceError(error, propertyKey, args, finallyCondition);
      }
    };
    return descriptor;
  };
};

export default ServiceErrorWrapper;
